import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { CloudDownload, Eye, KeyRound, Link2, Loader2, LogIn, Pause, Play, Server, Trash2, X } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/components/language-provider";
import { queryClient } from "@/lib/queryClient";
import { ACTIVE_JOB, RemoteError, remoteApi, type RemoteConnection, type RemoteJob, type RemoteOverview, type RemoteProvider } from "@/lib/remote-import-client";

const KEY = ["/api/documents/imports/remote"];
const fill = (s: string, vars: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ""));

/** Brand-neutral initials badge per provider (no logos). */
const MARK: Record<string, string> = { docusign: "DS", clicksign: "CS", adobesign: "AS", gdrive: "GD", onedrive: "OD" };

const STATUS_STYLE: Record<string, string> = {
  queued: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900",
  running: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900",
  waiting: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
  paused: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/60 dark:text-slate-300 dark:border-slate-800",
  reconnect: "bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900",
  failed: "bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900/60 dark:text-slate-400 dark:border-slate-800",
};

/**
 * Documents → Importar → "Direto de outro sistema": connect an account and let the server import every completed
 * document. The page can be closed; while it is open and the server tick is down, the page moves the jobs itself.
 */
export function RemoteImportCard({ onImported }: { onImported?: () => void }) {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const locale = language === "pt" ? "pt-BR" : "en-US";
  // Super admins get the link to turn a system on (Admin > Importação); customers get what to do meanwhile.
  const isSuperAdmin = (() => { try { return !!JSON.parse(localStorage.getItem("user") || "{}").isSuperAdmin; } catch { return false; } })();
  const nf = (n: number) => n.toLocaleString(locale);

  const { data, isLoading, error } = useQuery<RemoteOverview>({
    queryKey: KEY,
    queryFn: remoteApi.overview,
    refetchInterval: (q) => (q.state.data?.jobs.some((j) => ACTIVE_JOB.includes(j.status)) ? 10_000 : false),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });

  const [tokenFor, setTokenFor] = useState<RemoteProvider | null>(null);
  const [token, setToken] = useState("");
  const [sandbox, setSandbox] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [startFor, setStartFor] = useState<RemoteConnection | null>(null);
  const [folder, setFolder] = useState("");
  const [fromDate, setFromDate] = useState("");

  // Back from the provider's sign-in page.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const r = q.get("remote");
    if (!r) return;
    if (r === "connected") toast({ title: t("remote.connected") });
    else toast({ title: t("remote.connectFailed"), variant: "destructive" });
    q.delete("remote"); q.delete("provider"); q.delete("reason");
    window.history.replaceState(null, "", window.location.pathname + (q.toString() ? `?${q}` : ""));
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Without the server tick (cron), the open page keeps the jobs moving: one short slice every 20 s.
  const running = useRef(false);
  const active = !!data?.jobs.some((j) => ACTIVE_JOB.includes(j.status));
  const lastImported = useRef<number | null>(null);
  useEffect(() => {
    if (!data || data.tickHealthy || !active) return;
    const id = window.setInterval(async () => {
      if (running.current) return;
      running.current = true;
      try { await remoteApi.run(); } catch { /* next round */ } finally { running.current = false; refresh(); }
    }, 20_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.tickHealthy, active]);
  useEffect(() => {
    const total = data?.jobs.reduce((s, j) => s + j.imported, 0) ?? 0;
    if (lastImported.current !== null && total > lastImported.current) onImported?.();
    lastImported.current = total;
  }, [data, onImported]);

  const fail = (e: unknown) => toast({ title: e instanceof RemoteError ? e.message : t("remote.error"), variant: "destructive" });

  const signIn = async (p: RemoteProvider) => {
    setBusy(p.id);
    try { window.location.href = (await remoteApi.connectUrl(p.id)).url; } catch (e) { fail(e); setBusy(null); }
  };
  const saveToken = async () => {
    if (!tokenFor || !token.trim()) return;
    setBusy("token");
    try {
      await remoteApi.connectToken(tokenFor.id, token.trim(), sandbox);
      toast({ title: t("remote.connected") });
      setTokenFor(null); setToken(""); setSandbox(false);
      refresh();
    } catch (e) { fail(e); } finally { setBusy(null); }
  };
  const start = async () => {
    if (!startFor) return;
    setBusy("start");
    try {
      await remoteApi.createJob({ connectionId: startFor.id, folder: folder.trim() || undefined, fromDate: fromDate || undefined });
      toast({ title: t("remote.started"), description: t("remote.startedDesc") });
      setStartFor(null); setFolder(""); setFromDate("");
      refresh();
      onImported?.();
    } catch (e) { fail(e); } finally { setBusy(null); }
  };
  const control = async (job: RemoteJob, op: "pause" | "resume" | "cancel") => {
    setBusy(job.id);
    try { await remoteApi.control(job.id, op); refresh(); } catch (e) { fail(e); } finally { setBusy(null); }
  };
  const disconnect = async (c: RemoteConnection) => {
    setBusy(c.id);
    try { await remoteApi.disconnect(c.id); refresh(); } catch (e) { fail(e); } finally { setBusy(null); }
  };

  const statusText = (j: RemoteJob) => {
    if (j.status === "waiting" && j.nextRunAt)
      return fill(t("remote.status.waitingUntil"), { time: new Date(j.nextRunAt).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) });
    return t(`remote.status.${j.status}`);
  };

  if (isLoading) return <Card className="mt-6"><CardContent className="flex items-center gap-3 py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />{t("common.loading")}</CardContent></Card>;
  if (error || !data || !data.enabled) return null;

  return (
    <Card className="mt-6" data-testid="remote-import">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><CloudDownload className="h-5 w-5" />{t("remote.title")}</CardTitle>
        <CardDescription>{t("remote.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.providers.map((p) => {
            const conns = data.connections.filter((c) => c.provider === p.id);
            const canConnect = p.oauth || p.token;
            return (
              <div key={p.id} className="flex flex-col gap-3 rounded-lg border p-3" data-testid={`remote-provider-${p.id}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold" aria-hidden>{MARK[p.id] ?? "?"}</span>
                  <div className="min-w-0">
                    <p className="font-medium leading-tight">{p.label}</p>
                    <p className="text-xs text-muted-foreground">{t(p.kind === "esign" ? "remote.kind.esign" : "remote.kind.drive")}</p>
                  </div>
                </div>
                {conns.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 px-2 py-1.5 text-sm">
                    <span className="min-w-0 flex-1 truncate" title={c.account ?? undefined}>{c.account || t("remote.account")}</span>
                    {c.status === "reconnect" ? (
                      <span className="text-xs font-medium text-destructive">{t("remote.reconnect")}</span>
                    ) : (
                      <Button size="sm" className="h-7" onClick={() => { setStartFor(c); setFolder(""); setFromDate(""); }} data-testid={`remote-start-${c.id}`}>
                        {t("remote.import")}
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" aria-label={t("remote.disconnect")} disabled={busy === c.id} onClick={() => void disconnect(c)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <div className="mt-auto flex flex-wrap gap-2">
                  {p.oauth && (
                    <Button size="sm" variant={conns.length ? "outline" : "default"} disabled={busy === p.id} onClick={() => void signIn(p)} data-testid={`remote-signin-${p.id}`}>
                      {busy === p.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <LogIn className="mr-1.5 h-4 w-4" />}{t("remote.signIn")}
                    </Button>
                  )}
                  {p.token && (
                    <Button size="sm" variant="outline" onClick={() => { setTokenFor(p); setToken(""); setSandbox(false); }} data-testid={`remote-token-${p.id}`}>
                      <KeyRound className="mr-1.5 h-4 w-4" />{t("remote.useToken")}
                    </Button>
                  )}
                  {!canConnect && (
                    isSuperAdmin ? (
                      <a href="/admin?tab=imports" className="text-xs font-medium text-primary hover:underline" data-testid={`remote-activate-${p.id}`}>{t("remote.notConfiguredAdmin")}</a>
                    ) : (
                      <p className="text-xs text-muted-foreground">{t(p.id === "docusign" ? "remote.notConfiguredDocuSign" : "remote.notConfigured")}</p>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {tokenFor && (
          <div className="space-y-3 rounded-lg border p-4" data-testid="remote-token-form">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium">{fill(t("remote.tokenTitle"), { name: tokenFor.label })}</p>
                <p className="text-sm text-muted-foreground">{t(`remote.tokenHelp.${tokenFor.id}`)}</p>
              </div>
              <Button size="icon" variant="ghost" aria-label={t("common.cancel")} onClick={() => setTokenFor(null)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="remote-token">{t("remote.token")}</Label>
              <Input id="remote-token" type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} data-testid="input-remote-token" />
            </div>
            {tokenFor.id === "clicksign" && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={sandbox} onCheckedChange={(v) => setSandbox(v === true)} />{t("remote.sandbox")}
              </label>
            )}
            <Button onClick={() => void saveToken()} disabled={!token.trim() || busy === "token"} data-testid="button-save-remote-token">
              {busy === "token" && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t("remote.connect")}
            </Button>
          </div>
        )}

        {startFor && (
          <div className="space-y-3 rounded-lg border p-4" data-testid="remote-start-form">
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium">{fill(t("remote.startTitle"), { name: startFor.providerLabel, account: startFor.account ?? "" })}</p>
              <Button size="icon" variant="ghost" aria-label={t("common.cancel")} onClick={() => setStartFor(null)}><X className="h-4 w-4" /></Button>
            </div>
            {["gdrive", "onedrive"].includes(startFor.provider) ? (
              <div className="space-y-1.5">
                <Label htmlFor="remote-folder" className="flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" />{t(startFor.provider === "gdrive" ? "remote.folderLink" : "remote.folderPath")}</Label>
                <Input id="remote-folder" value={folder} onChange={(e) => setFolder(e.target.value)}
                  placeholder={startFor.provider === "gdrive" ? "https://drive.google.com/drive/folders/..." : "/Contratos/2024"} />
                <p className="text-xs text-muted-foreground">{t("remote.folderHelp")}</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="remote-from">{t("remote.fromDate")}</Label>
                <Input id="remote-from" type="date" className="w-auto" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                <p className="text-xs text-muted-foreground">{t("remote.fromDateHelp")}</p>
              </div>
            )}
            <Button onClick={() => void start()} disabled={busy === "start"} data-testid="button-start-remote">
              {busy === "start" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}{t("remote.start")}
            </Button>
          </div>
        )}

        {data.jobs.length > 0 && (
          <div className="space-y-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold"><Server className="h-4 w-4" />{t("remote.jobs")}</h3>
            {!data.tickHealthy && active && <p className="text-xs text-muted-foreground">{t("remote.pageDrives")}</p>}
            {data.jobs.map((j) => {
              const done = j.imported + j.duplicates + j.failed + j.skipped;
              const pct = j.expected > 0 ? Math.min(100, Math.round((done / j.expected) * 100)) : null;
              return (
                <div key={j.id} className="rounded-lg border p-3" data-testid={`remote-job-${j.id}`}>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium">{j.importName || j.providerLabel}</span>
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[j.status] ?? ""}`}>{statusText(j)}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {[j.source, j.account].filter(Boolean).join(" · ")}
                        {` · ${nf(j.imported)} ${t("import.imported").toLowerCase()}`}
                        {j.duplicates ? ` · ${nf(j.duplicates)} ${t("remote.duplicates")}` : ""}
                        {j.failed ? ` · ${nf(j.failed)} ${t("import.failed").toLowerCase()}` : ""}
                        {j.skipped ? ` · ${nf(j.skipped)} ${t("remote.skipped")}` : ""}
                        {j.expected ? ` · ${fill(t("remote.ofTotal"), { n: nf(j.expected) })}` : ""}
                      </p>
                      {j.status === "reconnect" && <p className="mt-1 text-xs text-destructive">{t("remote.reconnectHelp")}</p>}
                      {j.status === "failed" && j.lastError && <p className="mt-1 break-words text-xs text-destructive">{j.lastError}</p>}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {j.imported > 0 && (
                        <Link href={`/documents?import=${j.importId}`}><Button variant="ghost" size="sm"><Eye className="mr-1.5 h-4 w-4" />{t("import.view")}</Button></Link>
                      )}
                      {ACTIVE_JOB.includes(j.status) && (
                        <Button variant="ghost" size="sm" disabled={busy === j.id} onClick={() => void control(j, "pause")} data-testid={`remote-pause-${j.id}`}><Pause className="mr-1.5 h-4 w-4" />{t("remote.pause")}</Button>
                      )}
                      {["paused", "waiting", "failed"].includes(j.status) && (
                        <Button variant="ghost" size="sm" disabled={busy === j.id} onClick={() => void control(j, "resume")}><Play className="mr-1.5 h-4 w-4" />{t("remote.resume")}</Button>
                      )}
                      {!["completed", "cancelled", "failed"].includes(j.status) && (
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={busy === j.id} onClick={() => void control(j, "cancel")}><X className="mr-1.5 h-4 w-4" />{t("remote.cancel")}</Button>
                      )}
                    </div>
                  </div>
                  {pct !== null && ACTIVE_JOB.includes(j.status) && (
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted" aria-label={`${pct}%`}>
                      <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
